/**
 * API Client
 * Base HTTP client with authentication and error handling
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '@/types/auth';

const RAW_API_BASE = import.meta.env.VITE_API_URL as string | undefined;
const API_BASE_URL = RAW_API_BASE
  ? `${RAW_API_BASE.replace(/\/$/, '')}${RAW_API_BASE.endsWith('/api/v1') ? '' : '/api/v1'}`
  : '/api/v1';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
});

// Request interceptor: cookies are sent automatically, no need for Bearer token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Backend uses session cookies, not Authorization headers
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      // Clear local auth state (cookies are handled by browser)
      localStorage.removeItem('auth-storage');

      // Only redirect if not already on auth page
      if (!window.location.pathname.includes('/auth')) {
        window.location.href = '/auth';
      }
    }

    // Format error message
    const message =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'An unexpected error occurred';

    return Promise.reject(new Error(message));
  }
);

export default apiClient;

/**
 * Helper to get typed API errors
 */
export function getApiError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}
